import debounce from 'lodash.debounce'
import React, { useEffect, useState } from 'react'
import styled, { DefaultTheme, useTheme } from 'styled-components'
import defaultIcon from '../../assets/icons/defaultIcon.svg'
import userPositionIcon from '../../assets/icons/userPositionIcon.svg'
import {
  MAP_DEFAULT_ZOOM,
  MAP_MAX_ZOOM,
  MAP_MIN_ZOOM,
  MAP_BREAKPOINT_VALUE,
  MAP_ADVANCED_ZOOM,
  MAP_CITY_ZOOM,
  MAP_COUNTRY_ZOOM
} from '../../core/application/constants'
import {
  useAppProviderContext,
  useConfigurationContext,
  useDropOffLocationContext,
  useGeolocationContext,
  useMapContext
} from '../../core/application/contexts'
import { useMediaQuery } from '../../core/application/hooks'
import { breakpoints, deviceSizes } from '../../core/application/theme/breakpoints'
import DropOffProvider from '../../core/data/woosmap/DropOffProvider'
import {
  MapProps,
  WoosmapFeature,
  WoosmapMap,
  WoosmapMapEventListener,
  WoosmapMarker,
  WoosmapStoreOverlay,
  WoosmapStoreOverlayParams
} from '../../core/data/woosmap/woosmapSdk'
import { IDropOffTypeProvider } from '../../core/domain/providers/DropOffTypeProvider'
import Loader from '../loader/Loader'
import MapControllers from './MapControllers'
import SubmitDropoffButton from './SubmitDropoffButton'

const getOverlayParams = (theme: DefaultTheme, dropOffTypeProvider?: IDropOffTypeProvider): WoosmapStoreOverlayParams => {
  const iconUrl = dropOffTypeProvider
    ? dropOffTypeProvider?.generateIconUrl('default', theme.color.primary)
    : defaultIcon
  const rewardIconUrl = dropOffTypeProvider
    ? dropOffTypeProvider?.generateIconUrl('reward', theme.color.primary)
    : defaultIcon

  const defaultIconSize = {
    scaledSize: {
      height: 47,
      width: 34
    },
    anchor: {
      x: 17,
      y: 38
    }
  }

  const selectedIconSize = {
    scaledSize: {
      height: 60,
      width: 44
    },
    anchor: {
      x: 23,
      y: 49
    }
  }

  return {
    breakPoint: MAP_BREAKPOINT_VALUE,
    default: {
      color: `${theme.color.primary}`,
      size: 7.5,
      minSize: 1,
      icon: {
        url: iconUrl,
        ...defaultIconSize
      },
      selectedIcon: {
        url: iconUrl,
        ...selectedIconSize
      }
    },
    rules: [
      {
        color: `${theme.color.primary}`,
        type: 'reward',
        icon: {
          url: rewardIconUrl,
          ...defaultIconSize
        },
        selectedIcon: {
          url: rewardIconUrl,
          ...selectedIconSize
        }
      }
    ]
  }
}

const MapContainer: React.FC = () => {
  const theme = useTheme()
  const { dropOffTypeProvider } = useAppProviderContext()
  const {
    dropOffLocationList,
    setCurrentDropOffLocation,
    currentDropOffLocation,
    woosmapQuery,
    woosmapMapReady,
    setDisplaySubmitDropoffScreen
  } = useDropOffLocationContext()
  const { panToLocation, panTo, zoom, setZoom, recordPosition } = useMapContext()
  const { tenantConfiguration, tenantId } = useConfigurationContext()
  const { geolocation, isLoadingGeolocation, updateGeolocation } = useGeolocationContext()

  const isMobile = useMediaQuery(parseInt(deviceSizes.tablet))

  const [woosmapConfiguration, setWoosmapConfiguration] = useState<Partial<MapProps> | null>(null)
  const [woosmapMap, setWoosmapMap] = useState<WoosmapMap | null>(null)
  const [woosmapOverlay, setWoosmapOverlay] = useState<WoosmapStoreOverlay | null>(null)
  const [selectedOverlayFeature, setSelectedOverlayFeature] = useState<WoosmapFeature>()
  const [geolocMarker, setGeolocMarker] = useState<WoosmapMarker | undefined>(undefined)
  const [mapLoading, setMapLoading] = useState<boolean>(true)

  const handleIncreaseZoom = () => {
    setZoom((value) => Math.round(value + 1))
  }

  const handleDecreaseZoom = () => {
    setZoom((value) => Math.round(value - 1))
  }

  // init mapPosition on the on MapMoved
  useEffect(() => {
    if (woosmapMap) {
      const position = woosmapMap.getCenter()
      recordPosition({
        lat: position.lat(),
        lng: position.lng()
      })
    }
  }, [recordPosition, woosmapMap])

  // Display geolocation marker
  useEffect(() => {
    if (geolocMarker && woosmapMap) {
      geolocMarker.setMap(woosmapMap)
    }
  }, [geolocMarker, woosmapMap])

  // Wait for both configuration and geolocation to be ready
  useEffect(() => {
    if (!tenantConfiguration && tenantId) {
      return
    }
    if (!woosmapMapReady || woosmapConfiguration) {
      return
    }
    if (geolocation || !tenantConfiguration?.geolocationEnabled) {
      const zoom = tenantConfiguration?.startingZoomLevel || MAP_DEFAULT_ZOOM
      const lng = geolocation?.coordinates?.longitude || tenantConfiguration?.initialCoordinate?.longitude || 55.296249
      const lat = geolocation?.coordinates?.latitude || tenantConfiguration?.initialCoordinate?.latitude || 25.276987

      recordPosition({ lat, lng })

      const mapColors = {
        land: '#FBF8EC',
        text: '#2c2b2b',
        roads: '#ffffff',
        highways: '#e5e5e5',
        building: '#a9a8a8',
        water: '#BDE0FC',
        parks: '#5dd579'
      }

      const mapProps: MapProps = {
        center: {
          lat,
          lng
        },
        disableTilt: true,
        zoom,
        disableDefaultUI: true,
        styles: [
          {
            elementType: 'geometry', // Land color
            stylers: [{ color: mapColors.land }]
          },
          {
            elementType: 'labels.icon', // Disable train station & road number icons
            stylers: [{ visibility: 'off' }]
          },
          {
            elementType: 'labels.text.fill', // Labels color (city names)
            stylers: [{ color: mapColors.text }]
          },
          {
            elementType: 'labels.text.stroke', // Stroke around labels (city names)
            stylers: [{ color: '#ffffff' }]
          },
          {
            featureType: 'road', // Roads color
            elementType: 'geometry',
            stylers: [{ color: mapColors.roads }]
          },
          {
            featureType: 'road.arterial', // Roads names color
            elementType: 'labels.text.fill',
            stylers: [{ color: mapColors.text }]
          },
          {
            featureType: 'road.highway', // Highways color
            elementType: 'geometry',
            stylers: [{ color: mapColors.highways }]
          },
          {
            featureType: 'road.highway',
            elementType: 'labels.text.fill',
            stylers: [{ color: mapColors.text }]
          },
          {
            featureType: 'road.local',
            elementType: 'labels.text.fill',
            stylers: [{ color: mapColors.text }]
          },
          {
            featureType: 'transit.line', // Train lines
            elementType: 'geometry',
            stylers: [{ color: mapColors.highways }]
          },
          {
            featureType: 'transit.station', // Things like an airport area
            elementType: 'geometry',
            stylers: [{ color: mapColors.land }] // Not highlighting airports/stations, same color as land
          },
          {
            featureType: 'water', // That wet thing with fishes inside
            elementType: 'geometry',
            stylers: [{ color: mapColors.water }]
          },
          {
            featureType: 'water',
            elementType: 'labels.text.fill',
            stylers: [{ color: mapColors.text }]
          },
          {
            featureType: 'poi.park',
            stylers: [
              {
                color: mapColors.parks
              }
            ]
          }, {
            featureType: 'landscape.man_made',
            stylers: [
              {
                color: mapColors.building
              }
            ]
          }
        ]
      }
      setWoosmapConfiguration(mapProps)
    }
  }, [tenantConfiguration, tenantId, geolocation, woosmapMapReady, woosmapConfiguration, recordPosition])

  // Create Woosmap map and Woosmap Overlay when sdk is loaded
  useEffect(() => {
    if (!woosmapMap && woosmapConfiguration) {
      setMapLoading(false)
      const map = new window.woosmap.map.Map(document.getElementById('map'), woosmapConfiguration)
      setWoosmapMap(map)
      setZoom(woosmapConfiguration.zoom || 1)
      const overlayParams: WoosmapStoreOverlayParams = getOverlayParams(theme, dropOffTypeProvider)
      const storeOverlay = new window.woosmap.map.StoresOverlay(overlayParams)
      storeOverlay.setMap(map)
      setWoosmapOverlay(storeOverlay)
    }
  }, [dropOffTypeProvider, setZoom, theme, woosmapConfiguration, woosmapMap])

  // Update Woosmap Map when zoom param changed
  useEffect(() => {
    if (woosmapMap) {
      woosmapMap.setZoom(zoom)
    }
  }, [zoom, woosmapMap])

  // Update Woosmap Map when user geolocation changed
  useEffect(() => {
    if (woosmapMap && geolocation?.coordinates) {
      const position = {
        lat: geolocation.coordinates.latitude,
        lng: geolocation.coordinates.longitude
      }
      woosmapMap.flyTo({
        center: position,
        zoom: MAP_ADVANCED_ZOOM
      })
      // Add position icon to the map
      if (!geolocMarker) {
        const marker = new window.woosmap.map.Marker({
          position,
          icon: {
            url: userPositionIcon,
            scaledSize: {
              height: 26,
              width: 26
            }
          }
        })
        setGeolocMarker(marker)
      }
    }
  }, [geolocation, woosmapMap, geolocMarker])

  useEffect(() => {
    if (panToLocation && woosmapMap) {
      let zoom = MAP_ADVANCED_ZOOM
      if (panToLocation.locationType === 'country') {
        zoom = MAP_COUNTRY_ZOOM
      } else if (panToLocation.locationType === 'city') {
        zoom = MAP_CITY_ZOOM
      }
      woosmapMap.flyTo({
        center: {
          lat: panToLocation.lat,
          lng: panToLocation.lng
        },
        zoom
      })

      panTo(null)
    }
  }, [panTo, panToLocation, woosmapMap])

  // Update Woosmap Overlay when the related query change
  useEffect(() => {
    if (woosmapOverlay) {
      woosmapOverlay.setQuery(woosmapQuery ?? '')
    }
  }, [woosmapOverlay, woosmapQuery])

  // Add listeners to map events
  useEffect(() => {
    let storeSelectedListener: WoosmapMapEventListener
    let storeUnselectedListener: WoosmapMapEventListener
    let mapZoomChangedListener: WoosmapMapEventListener
    let mapCenterChangeListener: WoosmapMapEventListener

    const storeSelectedCallback = (store: WoosmapFeature) => {
      setSelectedOverlayFeature(store)
      const dropOff = DropOffProvider.toDropOffLocation(store)
      setCurrentDropOffLocation(dropOff)
    }

    const storeUnselectedCallback = () => {
      setSelectedOverlayFeature(undefined)
      setCurrentDropOffLocation(undefined)
    }

    if (woosmapMap) {
      const zoomHasChanged = () => {
        const newZoom = woosmapMap.getZoom()
        setZoom(newZoom)
      }

      const mapCenterChangeListenerCallback = () => {
        const location = woosmapMap.getCenter()
        recordPosition({
          lat: location.lat(),
          lng: location.lng()
        })
      }

      const { event } = window.woosmap.map
      mapZoomChangedListener = event.addListener(woosmapMap, 'zoom_changed', debounce(zoomHasChanged, 500))
      mapCenterChangeListener = event.addListener(
        woosmapMap,
        'center_changed',
        debounce(mapCenterChangeListenerCallback, 500)
      )
    }

    if (woosmapMap && dropOffLocationList.length > 0) {
      storeSelectedListener = window.woosmap.map.event.addListener(woosmapMap, 'store_selected', storeSelectedCallback)
      storeUnselectedListener = window.woosmap.map.event.addListener(
        woosmapMap,
        'store_unselected',
        storeUnselectedCallback
      )
    }

    return () => {
      storeSelectedListener && storeSelectedListener.remove()
      storeUnselectedListener && storeUnselectedListener.remove()
      mapZoomChangedListener && mapZoomChangedListener.remove()
      mapCenterChangeListener && mapCenterChangeListener.remove()
    }
  }, [woosmapMap, dropOffLocationList, currentDropOffLocation, setCurrentDropOffLocation, setZoom, recordPosition])

  useEffect(() => {
    if (woosmapMap && woosmapOverlay && currentDropOffLocation && selectedOverlayFeature === undefined) {
      woosmapMap.flyTo({
        center: currentDropOffLocation.getPosition(),
        zoom: MAP_ADVANCED_ZOOM
      })
      woosmapOverlay.setSelection(currentDropOffLocation.toWoosmapFeature())
    }
    if (woosmapOverlay && currentDropOffLocation === undefined) {
      woosmapOverlay.clearSelection()
    }
  }, [woosmapOverlay, woosmapMap, currentDropOffLocation, selectedOverlayFeature])

  // Pass geoloc update callback to controller only if geoloc is available
  const updateGeolocationCallback =
    tenantConfiguration?.geolocationEnabled && geolocation?.isSupportedByDevice && geolocation.authorized
      ? updateGeolocation
      : undefined


  const onSubmitDropoff = () => {
    setDisplaySubmitDropoffScreen(true)
  }

  return (
    <Container>
      {mapLoading && (
        <LoaderWrapper>
          <Loader color={theme.color.primary} />
        </LoaderWrapper>
      )}
      <MapWrapper id='map' />
      <ControllerWrapper>
        <MapControllers
          geolocation={updateGeolocationCallback}
          increaseZoom={handleIncreaseZoom}
          decreaseZoom={handleDecreaseZoom}
          isGeolocationLoading={isLoadingGeolocation}
          currentZoom={zoom}
          maxZoom={MAP_MAX_ZOOM}
          minZoom={tenantConfiguration?.minZoomLevel || MAP_MIN_ZOOM}
        />
      </ControllerWrapper>
      <SubmitDropoffWrapper>
        {!isMobile && tenantConfiguration?.dropOffSubmitEnabled && <SubmitDropoffButton onClick={onSubmitDropoff} />}
      </SubmitDropoffWrapper>
    </Container>
  )
}

const LoaderWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  background: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
`

const Container = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`

const MapWrapper = styled.div`
  width: 100%;
  height: 100%;
  z-index: 1;
`

const ControllerWrapper = styled.div`
  position: absolute;
  z-index: 2;
  top: 0;
  bottom: 0;
  right: 14px;

  height: 50%;

  @media ${breakpoints.tablet} {
    top: 0;
    bottom: 0;
    right: 24px;
  }
`

const SubmitDropoffWrapper = styled.div`
  position: absolute;
  z-index: 2;
  top: 14px;
  right: 14px;
`

export default MapContainer
